import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../lib/apiClient';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Lightbulb, Save, Loader2 } from 'lucide-react';

const PhilosophyManager = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statement, setStatement] = useState('');
  const [principlesText, setPrinciplesText] = useState('');

  const parsePrinciples = (value) => {
    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    return lines.map((line) => {
      const [title, description] = line.split('|');
      if (!title || !description) {
        throw new Error('Each principle must be formatted as "Title | Description".');
      }
      return { title: title.trim(), description: description.trim() };
    });
  };

  const serializePrinciples = (value = []) =>
    value.map((item) => `${item.title} | ${item.description}`).join('\n');

  const loadPhilosophy = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/philosophy');
      if (data) {
        setStatement(data.statement || '');
        setPrinciplesText(serializePrinciples(data.principles || []));
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load philosophy', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPhilosophy();
  }, [loadPhilosophy]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        statement,
        principles: parsePrinciples(principlesText),
      };
      await apiClient.put('/api/admin/philosophy', payload);
      toast({ title: 'Success', description: 'Philosophy updated successfully' });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="philosophy-manager">
      <div className="flex items-center gap-3">
        <Lightbulb className="w-8 h-8 text-cyan-500" />
        <h1 className="text-3xl font-bold text-white">Philosophy Management</h1>
      </div>

      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">Statement</label>
            <Textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              className="bg-neutral-800 border-neutral-700 text-white"
              rows={4}
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">Principles (one per line)</label>
            <Textarea
              value={principlesText}
              onChange={(e) => setPrinciplesText(e.target.value)}
              className="bg-neutral-800 border-neutral-700 text-white"
              rows={8}
              placeholder="Understand How Data Flows | Map data movement before coding"
              required
            />
            <p className="text-xs text-neutral-500">Format: Title | Description</p>
          </div>
          <Button type="submit" disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 text-black">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" />Save Philosophy</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PhilosophyManager;
